import { FileText, Image, UploadCloud, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import type { FieldErrors, UseFormSetValue, UseFormWatch } from "react-hook-form";
import type { TicketFormData } from "../config/schemas";
import { TICKET_ACCEPT_MIME, TICKET_MAX_FILE_SIZE } from "../config/schemas";
import { formatFileSize } from "../utils";

interface TicketAttachmentUploadProps {
  errors: FieldErrors<TicketFormData>;
  watch: UseFormWatch<TicketFormData>;
  setValue: UseFormSetValue<TicketFormData>;
}

export function TicketAttachmentUpload({ errors, watch, setValue }: TicketAttachmentUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const attachments = watch("attachments") ?? [];

  const addFiles = useCallback(
    (fileList: FileList) => {
      const validFiles: File[] = [];
      const fileErrors: string[] = [];

      for (const file of Array.from(fileList)) {
        if (file.size > TICKET_MAX_FILE_SIZE) {
          fileErrors.push(`${file.name} : trop volumineux (max 10 Mo)`);
          continue;
        }
        const allowedMimes = TICKET_ACCEPT_MIME.split(",");
        if (!allowedMimes.includes(file.type)) {
          fileErrors.push(`${file.name} : type non autorisé`);
          continue;
        }
        validFiles.push(file);
      }

      setLocalError(fileErrors.length > 0 ? fileErrors.join(" · ") : null);

      if (validFiles.length > 0) {
        const current = watch("attachments") ?? [];
        setValue("attachments", [...(current ?? []), ...validFiles], { shouldValidate: true });
      }
    },
    [watch, setValue],
  );

  const removeFile = useCallback(
    (index: number) => {
      const current = watch("attachments") ?? [];
      setValue(
        "attachments",
        current.filter((_, i) => i !== index),
        { shouldValidate: true },
      );
    },
    [watch, setValue],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files);
        e.target.value = "";
      }
    },
    [addFiles],
  );

  return (
    <div className="space-y-3">
      {/* Existing file list */}
      {attachments.length > 0 && (
        <div className="space-y-1.5">
          {attachments.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center gap-2.5 p-2 bg-base-200 border border-base-300/60 rounded-lg animate-in fade-in slide-in-from-top-1 duration-200"
            >
              {/* Thumbnail */}
              <div className="w-9 h-9 rounded bg-base-300/50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {file.type.startsWith("image/") ? (
                  <Image className="w-5 h-5 text-primary/60" />
                ) : (
                  <FileText className="w-5 h-5 text-base-content/30" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-base-content truncate">{file.name}</div>
                <div className="text-[10px] text-base-content/40 font-mono">
                  {formatFileSize(file.size)}
                </div>
              </div>

              {/* Remove */}
              <button
                type="button"
                className="btn btn-ghost btn-xs text-base-content/30 hover:text-error hover:bg-error/10 rounded"
                onClick={() => removeFile(index)}
                aria-label={`Supprimer ${file.name}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-5 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all duration-200 ${
          isDragOver
            ? "border-primary bg-primary/10 shadow-[0_0_0_3px_oklch(var(--p)_/_0.15)]"
            : "border-base-300 bg-base-200/30 hover:border-primary/50 hover:bg-primary/5"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <UploadCloud
          className={`w-8 h-8 transition-colors duration-200 ${
            isDragOver ? "text-primary" : "text-base-content/30"
          }`}
        />
        <p className="text-xs text-base-content/50">
          Glissez vos fichiers ici ou <span className="font-semibold text-primary">parcourir</span>
        </p>
        <p className="text-[10px] text-base-content/30 font-mono">
          PNG, JPG, JPEG, GIF, WebP, PDF, TXT, CSV — max 10 Mo/fichier
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        accept={TICKET_ACCEPT_MIME}
        onChange={handleInputChange}
      />

      {/* Error */}
      {(errors?.attachments?.message || localError) && (
        <p className="text-xs text-error">{errors?.attachments?.message ?? localError}</p>
      )}
    </div>
  );
}
